import { useState } from "react"

import { Datasets } from "../../../../constants/enums"
import { parseErrorAsObj } from "../../../../errors/errorProcessors"
import { IAppError } from "../../../../errors/errorTypes"
import { useV2gCommentRouting } from "../../../../hooks/useV2gCommentRouting"
import { deleteClientProtected } from "../../clients/clients"
import {
    TTR_DELETE_COMMENT,
    LCT_DELETE_COMMENT,
    V2G_DELETE_LCT_COMMENT,
} from "../../urls/private"

interface IDeleteCommentHook {
    isSending: boolean
    error: IAppError | undefined
    postResponse: (
        deviceSystemRef: string,
        comment: string,
        dataset?: Datasets
    ) => Promise<void>
}

/**
 * Provides functions and logic for posting a delete comment request
 * on a TTR/LCT device. Pass `isV2gDevice` when the device is a V2G EVCP so
 * that V2G assessors are routed to the `/v2g/v1/` endpoints.
 */
const useDeleteComment = (isV2gDevice = false): IDeleteCommentHook => {
    const [isSending, setIsSending] = useState(false)
    const [error, setError] = useState<IAppError | undefined>(undefined)
    const useAsV2gAssessor = useV2gCommentRouting(isV2gDevice)

    const postResponse = async (
        deviceSystemRef: string,
        commentId: string,
        dataset?: Datasets
    ): Promise<void> => {
        try {
            setIsSending(true)

            if (
                dataset &&
                ![Datasets.TTR, Datasets.TTR_DRAFT].includes(dataset)
            ) {
                const url = useAsV2gAssessor
                    ? V2G_DELETE_LCT_COMMENT(
                          deviceSystemRef,
                          commentId,
                          dataset
                      )
                    : LCT_DELETE_COMMENT(deviceSystemRef, commentId, dataset)
                await deleteClientProtected(url)
            } else {
                await deleteClientProtected(
                    TTR_DELETE_COMMENT(deviceSystemRef, commentId)
                )
            }
            setError(undefined)
        } catch (err) {
            const errorObj = parseErrorAsObj(err)
            setError(errorObj)
        } finally {
            setIsSending(false)
        }
    }

    return {
        error,
        isSending,
        postResponse,
    }
}

export default useDeleteComment
